import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/utils/translations";
import { localizePath } from "@/utils/languageRoutes";
import { getOptimizedImageUrl } from "@/utils/imageUtils";

interface DemoHeroProps {
  language: string;
}

const CORRIDOR_PATH = "M 440 90 Q 430 300 140 380";

const achievements = [
  {
    title: "Global Business Partner",
    titleKo: "글로벌 비즈니스 파트너",
    organization: "Small & Medium Business Administration",
    organizationKo: "중소벤처기업부",
    since: 2022,
    logo: "https://scmtfyjpaivqbafbtooy.supabase.co/storage/v1/object/public/dcl_website_asset/trust_banner/kr_smba_logo.gif",
    position: "left-0 top-[6%]",
  },
  {
    title: "Global ICT Portal Local Expert",
    titleKo: "글로벌 ICT 포털 현지 전문가",
    organization: "National IT Industry Promotion Agency",
    organizationKo: "정보통신산업진흥원 (NIPA)",
    since: 2021,
    logo: "https://scmtfyjpaivqbafbtooy.supabase.co/storage/v1/object/public/dcl_website_asset/trust_banner/nipa_logo.png",
    position: "right-0 top-[38%]",
  },
  {
    title: "Global Regional Expert",
    titleKo: "글로벌 지역 전문가",
    organization: "Korea Trade-Investment Promotion Agency",
    organizationKo: "대한무역투자진흥공사 (KOTRA)",
    since: 2019,
    logo: "https://scmtfyjpaivqbafbtooy.supabase.co/storage/v1/object/public/dcl_website_asset/trust_banner/kotra_logo.png",
    position: "left-[2%] top-[52%]",
  },
  {
    title: "Recruiting Agent",
    titleKo: "공식 채용 에이전트",
    organization: "Ministry of External Affairs, Gov't of India",
    organizationKo: "인도 정부 외교부",
    since: 2019,
    logo: "https://scmtfyjpaivqbafbtooy.supabase.co/storage/v1/object/public/dcl_website_asset/trust_banner/Ministry_of_External_Affairs_India.svg.png",
    position: "right-[4%] bottom-[2%]",
  },
];

const ROTATION_MS = 2400;

const DemoHero = ({ language }: DemoHeroProps) => {
  const t = useTranslation(language);
  const isKo = language === "KO";

  const rotatingWords = isKo
    ? ["법인 설립", "사업 셋업", "인력 채용", "회계·세무"]
    : ["Incorporation", "Compliance", "Recruitment", "Accounting"];

  const [wordIndex, setWordIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setWordIndex((i) => (i + 1) % rotatingWords.length), ROTATION_MS);
    return () => clearInterval(id);
  }, [rotatingWords.length]);

  // Mouse parallax for the corridor scene
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const parallaxX = useSpring(useTransform(mouseX, [-0.5, 0.5], [10, -10]), { stiffness: 60, damping: 15 });
  const parallaxY = useSpring(useTransform(mouseY, [-0.5, 0.5], [8, -8]), { stiffness: 60, damping: 15 });

  const headlineLine1 = isKo ? "인도 시장 진출," : "Enter the Indian market.";
  const staggeredWords = headlineLine1.split(" ");

  return (
    <section
      className="relative flex min-h-[100dvh] w-full flex-col overflow-hidden bg-[#FAFAF8]"
      onMouseMove={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        mouseX.set((event.clientX - rect.left) / rect.width - 0.5);
        mouseY.set((event.clientY - rect.top) / rect.height - 0.5);
      }}
    >
      {/* ——— Atmosphere: wireframe globe, slowly turning ——— */}
      <motion.svg
        aria-hidden
        viewBox="0 0 800 800"
        fill="none"
        className="absolute -right-[12%] -top-[30%] h-[820px] w-[820px] text-slate-300/60 md:h-[1000px] md:w-[1000px]"
        animate={{ rotate: 360 }}
        transition={{ duration: 180, repeat: Infinity, ease: "linear" }}
      >
        <g stroke="currentColor" strokeWidth="1">
          <circle cx="400" cy="400" r="378" strokeDasharray="3 7" />
          <circle cx="400" cy="400" r="378" opacity="0.5" />
          <ellipse cx="400" cy="400" rx="378" ry="252" opacity="0.6" />
          <ellipse cx="400" cy="400" rx="378" ry="128" opacity="0.6" />
          <ellipse cx="400" cy="400" rx="252" ry="378" opacity="0.6" />
          <ellipse cx="400" cy="400" rx="128" ry="378" opacity="0.6" />
          <line x1="22" y1="400" x2="778" y2="400" opacity="0.5" />
        </g>
      </motion.svg>
      <motion.div
        aria-hidden
        className="absolute -top-32 right-[-8%] h-[520px] w-[520px] rounded-full bg-sky-200/40 blur-3xl"
        animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute bottom-[-20%] left-[-6%] h-[460px] w-[460px] rounded-full bg-amber-200/30 blur-3xl"
        animate={{ x: [0, 50, 0], y: [0, -24, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="container relative z-10 mx-auto flex flex-1 items-center px-4 pt-24 pb-10">
        <div className="grid w-full items-center gap-14 lg:grid-cols-[1fr_1fr] lg:gap-8">
          {/* ——— Left: kinetic headline ——— */}
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white/80 px-4 py-1.5 shadow-sm backdrop-blur"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-secondary" />
              </span>
              <span className="text-sm font-medium text-slate-700">{t.hero.badge}</span>
              <span aria-hidden className="h-3.5 w-px bg-slate-200" />
              <span className="text-sm font-medium text-primary">{isKo ? "2018년부터" : "Since 2018"}</span>
            </motion.div>

            <h1 className="mt-8 text-[2.6rem] font-bold leading-[1.06] tracking-tight text-slate-900 md:text-6xl xl:text-7xl">
              <span className="block">
                {staggeredWords.map((word, index) => (
                  <motion.span
                    key={`${word}-${index}`}
                    className="mr-[0.28em] inline-block"
                    initial={{ opacity: 0, y: 28, filter: "blur(8px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{ duration: 0.6, delay: 0.15 + index * 0.09, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {word}
                  </motion.span>
                ))}
              </span>
              <motion.span
                className="mt-2 block"
                initial={{ opacity: 0, y: 28, filter: "blur(8px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                {!isKo && <span>We handle&nbsp;</span>}
                {/* inline-grid with invisible copies reserves the widest word's width — no reflow while rotating */}
                <span className="relative inline-grid overflow-hidden align-bottom">
                  {rotatingWords.map((word) => (
                    <span key={word} aria-hidden className="invisible col-start-1 row-start-1 whitespace-nowrap">
                      {word}
                    </span>
                  ))}
                  <AnimatePresence mode="popLayout" initial={false}>
                    <motion.span
                      key={rotatingWords[wordIndex]}
                      className="col-start-1 row-start-1 whitespace-nowrap bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent"
                      initial={{ y: "105%", opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: "-105%", opacity: 0 }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {rotatingWords[wordIndex]}
                    </motion.span>
                  </AnimatePresence>
                  <motion.span
                    aria-hidden
                    className="absolute bottom-[0.04em] left-0 h-[0.1em] w-full origin-left rounded-full bg-secondary"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.6, delay: 0.9, ease: "easeOut" }}
                  />
                </span>
                {isKo && <span>까지 원스탑으로</span>}
              </motion.span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-7 max-w-xl whitespace-pre-line text-lg leading-relaxed text-slate-600 md:text-xl"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.85 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <Link to={localizePath("/contact", language)}>
                <Button
                  size="lg"
                  className="group relative w-full overflow-hidden rounded-xl bg-primary px-7 py-6 text-base font-semibold text-white shadow-lg shadow-primary/25 transition-all hover:bg-primary-dark hover:shadow-xl hover:shadow-primary/30 sm:w-auto"
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 -translate-x-[130%] bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[130%]"
                  />
                  {isKo ? "전문가와 상담하기" : "Talk to an Expert"}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to={localizePath("/about", language)}>
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full rounded-xl border-slate-200 bg-white/70 px-7 py-6 text-base font-medium text-slate-700 backdrop-blur transition-colors hover:border-primary/30 hover:bg-white hover:text-primary sm:w-auto"
                >
                  {isKo ? "자세히 알아보기" : "Learn More"}
                </Button>
              </Link>
            </motion.div>
          </div>

          {/* ——— Right: Seoul → Bengaluru corridor scene ——— */}
          <motion.div
            style={{ x: parallaxX, y: parallaxY }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative mx-auto aspect-[560/500] w-full max-w-[560px]"
          >
            <svg viewBox="0 0 560 500" fill="none" className="absolute inset-0 h-full w-full">
              <defs>
                <linearGradient id="demo-corridor" x1="440" y1="90" x2="140" y2="380" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#1434A4" />
                  <stop offset="1" stopColor="#FF9F1C" />
                </linearGradient>
              </defs>
              {/* marching dashed route */}
              <path
                d={CORRIDOR_PATH}
                stroke="#CBD5E1"
                strokeWidth="2"
                strokeDasharray="6 10"
                strokeLinecap="round"
                style={{ animation: "demo-dash 2.4s linear infinite" }}
              />
              {/* gradient route draws itself once */}
              <motion.path
                d={CORRIDOR_PATH}
                stroke="url(#demo-corridor)"
                strokeWidth="2.5"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.85 }}
                transition={{ duration: 1.6, delay: 0.9, ease: "easeInOut" }}
              />
              {/* travelling pulse along the corridor */}
              <g>
                <circle r="10" fill="#FF9F1C" opacity="0.18">
                  <animateMotion dur="4.5s" repeatCount="indefinite" path={CORRIDOR_PATH} />
                </circle>
                <circle r="4.5" fill="#FF9F1C">
                  <animateMotion dur="4.5s" repeatCount="indefinite" path={CORRIDOR_PATH} />
                </circle>
              </g>
              {/* endpoints */}
              <circle cx="440" cy="90" r="5" fill="#1434A4" />
              <circle cx="140" cy="380" r="5" fill="#FF9F1C" />
            </svg>

            {/* city nodes */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1 }}
              className="absolute left-[78.5%] top-[18%] flex -translate-x-1/2 -translate-y-[130%] items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 shadow-md"
            >
              <span role="img" aria-label="South Korea flag">🇰🇷</span>
              <span className="text-sm font-semibold text-slate-800">{isKo ? "서울" : "Seoul"}</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.2 }}
              className="absolute left-[25%] top-[76%] flex -translate-x-1/2 translate-y-[40%] items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 shadow-md"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-secondary" />
              </span>
              <span role="img" aria-label="India flag">🇮🇳</span>
              <span className="text-sm font-semibold text-slate-800">{isKo ? "벵갈루루" : "Bengaluru"}</span>
            </motion.div>

            {/* floating credential chips */}
            {achievements.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.1 + index * 0.15 }}
                className={`absolute ${item.position} w-[46%] min-w-[180px] max-w-[250px]`}
              >
                <motion.div
                  animate={{ y: [0, -7, 0] }}
                  transition={{ duration: 4.5 + index, repeat: Infinity, ease: "easeInOut", delay: index * 0.7 }}
                  className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/85 p-3 shadow-[0_8px_30px_rgb(0,0,0,0.06)] backdrop-blur-md transition-shadow hover:shadow-[0_8px_30px_rgb(20,52,164,0.12)]"
                >
                  <div className="flex h-10 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-100 bg-white p-1.5">
                    <img
                      src={getOptimizedImageUrl(item.logo, { width: 120, quality: 85 })}
                      alt={`${item.organization} logo`}
                      className="max-h-full max-w-full object-contain"
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-semibold leading-tight text-slate-900">
                      {isKo ? item.titleKo : item.title}
                    </p>
                    <p className="mt-0.5 truncate text-[11px] text-slate-500">
                      {isKo ? item.organizationKo : item.organization}
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ——— Accreditation marquee ——— */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.4 }}
        className="relative z-10 border-t border-slate-200/80 bg-white/60 backdrop-blur"
      >
        <div className="container mx-auto flex flex-col items-center gap-3 px-4 py-5 md:flex-row md:gap-8">
          <p className="flex shrink-0 items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            <BadgeCheck className="h-4 w-4 text-primary" />
            {isKo ? "공식 인증 및 지정 기관" : "Accredited & recognized by"}
          </p>
          <div className="demo-marquee relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,white_8%,white_92%,transparent)]">
            <div className="demo-marquee-track flex w-max items-center gap-14 pr-14">
              {[...achievements, ...achievements].map((item, index) => (
                <div
                  key={`marquee-${index}`}
                  aria-hidden={index >= achievements.length}
                  className="flex items-center gap-3 opacity-70 transition-opacity hover:opacity-100"
                >
                  <img
                    src={getOptimizedImageUrl(item.logo, { width: 96, quality: 85 })}
                    alt={index < achievements.length ? `${item.organization} logo` : ""}
                    className="h-7 w-auto object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="whitespace-nowrap text-sm text-slate-600">
                    {isKo ? item.organizationKo : item.organization}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default DemoHero;
